import { supabase } from '../lib/supabase';
import { BlindsEnquiry, BlindAwningSpecification, Attachment } from '../types';
import type { DatabaseEnquiry, DatabaseSpecification, DatabaseAttachment } from '../lib/supabase';

export class SupabaseService {
  
  // Transform app data to database format
  private transformToDatabase(enquiry: BlindsEnquiry): Partial<DatabaseEnquiry> {
    // Helper function to handle empty date/time values
    const formatDate = (dateString: string) => {
      return dateString && dateString.trim() ? dateString : null;
    };

    const formatTime = (timeString: string) => {
      return timeString && timeString.trim() ? timeString : null;
    };

    return {
      id: enquiry.id,
      customer_name: enquiry.customerDetails.name,
      customer_address: enquiry.customerDetails.address || null,
      customer_phone: enquiry.customerDetails.phone || null,
      customer_mobile: enquiry.customerDetails.mobile || null,
      customer_email: enquiry.customerDetails.email || null,
      customer_how_heard: enquiry.customerDetails.howHeard || null,
      customer_reason_for_enquiry: enquiry.customerDetails.reasonForEnquiry || null,
      customer_notes: enquiry.customerDetails.notes || null,
      
      appointment_date: formatDate(enquiry.appointmentDetails.date),
      appointment_time: formatTime(enquiry.appointmentDetails.time),
      appointment_samples_to_take: enquiry.appointmentDetails.samplesToTake,
      appointment_description: enquiry.appointmentDetails.description || null,
      
      quote_date_sent: formatDate(enquiry.quoteDetails.dateSent),
      quote_method: enquiry.quoteDetails.method,
      quote_follow_up_date: formatDate(enquiry.quoteDetails.followUpDate),
      quote_comments: enquiry.quoteDetails.comments || null,
      quote_staff_member: enquiry.quoteDetails.staffMember || null,
      
      outcome_status: enquiry.outcome.status,
      outcome_notes: enquiry.outcome.notes || null,
      outcome_flag_for_accounts: enquiry.outcome.flagForAccounts,
      
      updated_at: new Date().toISOString()
    } as Partial<DatabaseEnquiry>;
  }

  // Transform database data to app format
  private transformFromDatabase(
    dbEnquiry: DatabaseEnquiry, 
    specs: DatabaseSpecification[] = [], 
    attachments: DatabaseAttachment[] = []
  ): BlindsEnquiry {
    return {
      id: dbEnquiry.id,
      customerDetails: {
        name: dbEnquiry.customer_name || '',
        address: dbEnquiry.customer_address || '',
        phone: dbEnquiry.customer_phone || '',
        mobile: dbEnquiry.customer_mobile || '',
        email: dbEnquiry.customer_email || '',
        howHeard: dbEnquiry.customer_how_heard || '',
        reasonForEnquiry: dbEnquiry.customer_reason_for_enquiry || '',
        notes: dbEnquiry.customer_notes || ''
      },
      appointmentDetails: {
        date: dbEnquiry.appointment_date || '',
        time: dbEnquiry.appointment_time || '',
        samplesToTake: dbEnquiry.appointment_samples_to_take || false,
        description: dbEnquiry.appointment_description || ''
      },
      quoteDetails: {
        dateSent: dbEnquiry.quote_date_sent || '',
        method: (dbEnquiry.quote_method as any) || 'Email',
        followUpDate: dbEnquiry.quote_follow_up_date || '',
        comments: dbEnquiry.quote_comments || '',
        staffMember: dbEnquiry.quote_staff_member || ''
      },
      outcome: {
        status: (dbEnquiry.outcome_status as any) || 'Successful',
        notes: dbEnquiry.outcome_notes || '',
        flagForAccounts: dbEnquiry.outcome_flag_for_accounts || false
      },
      specifications: specs.map(spec => ({
        id: spec.id,
        itemNumber: spec.item_number || '',
        product: spec.product || '',
        location: spec.location || '',
        quantity: spec.quantity || 1,
        fit: spec.fit || '',
        fixing: spec.fixing || '',
        width: spec.width || 0,
        drop: spec.drop || 0,
        material: spec.material || '',
        bottomRail: spec.bottom_rail || '',
        control: spec.control || '',
        stack: spec.stack || '',
        comments: spec.comments || ''
      })),
      attachments: [], // Attachments will be loaded separately
      createdAt: dbEnquiry.created_at,
      updatedAt: dbEnquiry.updated_at
    };
  }

  // Save enquiry to database
  async saveEnquiry(enquiry: BlindsEnquiry): Promise<{ success: boolean; error?: string }> {
    try {
      // Save main enquiry
      const enquiryData = this.transformToDatabase(enquiry);
      const { data: savedEnquiry, error: enquiryError } = await supabase
        .from('enquiries')
        .upsert(enquiryData)
        .select()
        .single();

      if (enquiryError) {
        console.error('Enquiry save error:', enquiryError);
        return { success: false, error: enquiryError.message };
      }

      // Save specifications
      if (enquiry.specifications.length > 0) {
        // Delete existing specs first
        await supabase
          .from('specifications')
          .delete()
          .eq('enquiry_id', enquiry.id);

        // Insert new specs
        const specsToInsert = enquiry.specifications.map(spec => ({
          enquiry_id: enquiry.id,
          item_number: spec.itemNumber,
          product: spec.product,
          location: spec.location,
          quantity: spec.quantity,
          fit: spec.fit,
          fixing: spec.fixing,
          width: spec.width,
          drop: spec.drop,
          material: spec.material,
          bottom_rail: spec.bottomRail,
          control: spec.control,
          stack: spec.stack,
          comments: spec.comments
        }));

        const { error: specsError } = await supabase
          .from('specifications')
          .insert(specsToInsert);

        if (specsError) {
          console.error('Specifications save error:', specsError);
          return { success: false, error: specsError.message };
        }
      }

      // Handle file uploads to Supabase Storage
      let uploadResults = [];
      console.log(`📋 Starting file upload process for ${enquiry.attachments.length} attachments`);
      
      for (const attachment of enquiry.attachments) {
        const fileName = `${enquiry.id}/${attachment.id}-${attachment.name}`;
        
        try {
          console.log(`📁 Uploading file: ${fileName}`);
          console.log(`📊 File details:`, {
            name: attachment.name,
            type: attachment.type,
            size: attachment.file.size,
            id: attachment.id
          });
          
          // Check if file object exists
          if (!attachment.file) {
            console.error('❌ No file object found for attachment:', attachment.name);
            uploadResults.push(`❌ No file data for ${attachment.name}`);
            continue;
          }
          
          // Upload file to Supabase Storage
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('enquiry-attachments')
            .upload(fileName, attachment.file, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('❌ File upload error:', uploadError);
            console.error('❌ Upload error details:', {
              message: uploadError.message,
              name: uploadError.name,
              stack: uploadError.stack
            });
            uploadResults.push(`❌ Failed to upload ${attachment.name}: ${uploadError.message}`);
            continue; // Continue with other files
          }

          console.log(`✅ File uploaded successfully: ${fileName}`);
          console.log(`📄 Upload response:`, uploadData);

          // Save attachment record
          const { error: attachmentError } = await supabase
            .from('attachments')
            .upsert({
              id: attachment.id,
              enquiry_id: enquiry.id,
              name: attachment.name,
              type: attachment.type,
              file_path: fileName,
              file_size: attachment.file.size
            });

          if (attachmentError) {
            console.error('❌ Attachment record error:', attachmentError);
            console.error('❌ Attachment record details:', {
              message: attachmentError.message,
              name: attachmentError.name,
              stack: attachmentError.stack
            });
            uploadResults.push(`❌ Failed to save ${attachment.name} record: ${attachmentError.message}`);
          } else {
            console.log(`✅ Attachment record saved: ${attachment.name}`);
            uploadResults.push(`✅ ${attachment.name} uploaded successfully`);
          }
        } catch (fileError) {
          console.error('💥 File processing error:', fileError);
          uploadResults.push(`❌ Error processing ${attachment.name}: ${fileError}`);
        }
      }

      // Report upload results
      if (uploadResults.length > 0) {
        console.log('📊 File upload results:', uploadResults);
      }

      return { success: true };
    } catch (error) {
      console.error('Save enquiry error:', error);
      return { success: false, error: 'Failed to save enquiry' };
    }
  }

  // Load enquiry from database
  async loadEnquiry(id: string): Promise<{ success: boolean; enquiry?: BlindsEnquiry; error?: string }> {
    try {
      // Load main enquiry
      const { data: enquiry, error: enquiryError } = await supabase
        .from('enquiries')
        .select('*')
        .eq('id', id)
        .single();

      if (enquiryError) {
        return { success: false, error: enquiryError.message };
      }

      // Load specifications
      const { data: specs, error: specsError } = await supabase
        .from('specifications')
        .select('*')
        .eq('enquiry_id', id)
        .order('created_at');

      if (specsError) {
        console.error('Specs load error:', specsError);
      }

      // Load attachments
      const { data: attachments, error: attachmentsError } = await supabase
        .from('attachments')
        .select('*')
        .eq('enquiry_id', id);

      if (attachmentsError) {
        console.error('Attachments load error:', attachmentsError);
      }

      const loadedEnquiry = this.transformFromDatabase(
        enquiry, 
        specs || [], 
        attachments || []
      );

      return { success: true, enquiry: loadedEnquiry };
    } catch (error) {
      console.error('Load enquiry error:', error);
      return { success: false, error: 'Failed to load enquiry' };
    }
  }

  // List all enquiries
  async loadAllEnquiries(): Promise<{ success: boolean; enquiries?: any[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('id, customer_name, created_at, outcome_status, updated_at')
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, enquiries: data || [] };
    } catch (error) {
      console.error('Load enquiries error:', error);
      return { success: false, error: 'Failed to load enquiries' };
    }
  }

  // Delete enquiry
  async deleteEnquiry(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('enquiries')
        .delete()
        .eq('id', id);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Delete enquiry error:', error);
      return { success: false, error: 'Failed to delete enquiry' };
    }
  }

  // Create storage bucket (run once) - PRIVATE bucket for security
  async initializeStorage(): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase.storage.createBucket('enquiry-attachments', {
        public: false, // Private bucket for better security
        allowedMimeTypes: [
          'image/jpeg', 'image/png', 'image/gif', 'image/webp',
          'application/pdf', 'application/msword', 
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'text/plain'
        ],
        fileSizeLimit: 10485760 // 10MB limit
      });

      if (error && error.message !== 'Bucket already exists') {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Storage initialization error:', error);
      return { success: false, error: 'Failed to initialize storage' };
    }
  }

  // Get signed URL for private file access
  async getFileUrl(filePath: string): Promise<string | null> {
    try {
      const { data, error } = await supabase.storage
        .from('enquiry-attachments')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) {
        console.error('Error creating signed URL:', error);
        return null;
      }

      return data.signedUrl;
    } catch (error) {
      console.error('Error getting file URL:', error);
      return null;
    }
  }

  // Test database connection
  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('enquiries')
        .select('count', { count: 'exact' });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Connection test error:', error);
      return { success: false, error: 'Failed to connect to database' };
    }
  }

  // Test storage bucket access
  async testStorageBucket(): Promise<{ success: boolean; error?: string; bucketInfo?: any }> {
    try {
      console.log('🪣 Testing storage bucket access...');
      console.log('🔗 Supabase URL:', process.env.REACT_APP_SUPABASE_URL);
      console.log('🔑 Using anon key:', process.env.REACT_APP_SUPABASE_ANON_KEY?.substring(0, 20) + '...');
      
      // First, try to list buckets to see what's available
      console.log('📋 Attempting to list all buckets...');
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      
      if (listError) {
        console.error('❌ List buckets error:', listError);
      } else {
        console.log('✅ Available buckets:', buckets?.map(b => ({ name: b.name, public: b.public })));
      }
      
      // Try to directly access the bucket by listing files
      console.log('📂 Attempting to list files in enquiry-attachments...');
      const { data: files, error: filesError } = await supabase.storage
        .from('enquiry-attachments')
        .list('', { limit: 1 });

      if (filesError) {
        console.error('❌ Storage bucket access error:', filesError);
        console.error('❌ Error details:', {
          message: filesError.message,
          name: filesError.name,
          stack: filesError.stack
        });
        
        // Check if it's a bucket not found error
        if (filesError.message.includes('not found') || filesError.message.includes('does not exist')) {
          return { 
            success: false, 
            error: `Storage bucket "enquiry-attachments" not found. Available buckets: ${buckets?.map(b => b.name).join(', ') || 'none'}`,
            bucketInfo: { 
              errorDetails: filesError.message,
              availableBuckets: buckets?.map(b => ({ name: b.name, public: b.public })) || []
            }
          };
        }
        
        return { 
          success: false, 
          error: `Cannot access bucket: ${filesError.message}`,
          bucketInfo: { 
            errorDetails: filesError.message,
            availableBuckets: buckets?.map(b => ({ name: b.name, public: b.public })) || []
          }
        };
      }

      console.log('✅ Successfully listed files:', files?.length || 0, 'files found');

      // Test write access by attempting to create a test file
      console.log('📝 Testing write access...');
      const testFileName = 'test-access.txt';
      const testFileContent = new Blob(['test'], { type: 'text/plain' });
      
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('enquiry-attachments')
        .upload(testFileName, testFileContent, { upsert: true });

      let canWrite = true;
      if (uploadError) {
        console.warn('⚠️ Write test failed:', uploadError);
        canWrite = false;
      } else {
        console.log('✅ Write test successful, uploaded:', uploadData?.path);
        
        // Clean up test file
        const { error: removeError } = await supabase.storage
          .from('enquiry-attachments')
          .remove([testFileName]);
        
        if (removeError) {
          console.warn('⚠️ Could not clean up test file:', removeError);
        } else {
          console.log('🧹 Test file cleaned up successfully');
        }
      }

      console.log('✅ Storage bucket test successful');
      
      // Find the actual bucket info from the list
      const actualBucket = buckets?.find(b => b.name === 'enquiry-attachments');
      
      return { 
        success: true, 
        bucketInfo: {
          name: 'enquiry-attachments',
          fileCount: files?.length || 0,
          canRead: true,
          canWrite: canWrite,
          isPublic: actualBucket?.public || false, // Use actual bucket info
          isPrivate: !actualBucket?.public, // Show if private
          actualBucketInfo: actualBucket,
          availableBuckets: buckets?.map(b => ({ name: b.name, public: b.public })) || []
        }
      };
    } catch (error) {
      console.error('💥 Storage test error:', error);
      return { success: false, error: `Failed to test storage bucket: ${error}` };
    }
  }
}

export const supabaseService = new SupabaseService();